import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  DatePicker,
  Select,
  Button,
  Card,
  Skeleton,
  Alert,
  Empty,
  Typography,
  Row,
  Col,
  Slider,
  Tag,
  Progress,
  Divider,
  Space,
  Statistic,
  Collapse
} from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  ProjectOutlined,
  RiseOutlined,
  FallOutlined,
  BulbOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { cardVariants, fadeInVariants, buttonVariants, containerVariants } from '../../utils/animations';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const MonthlySummary = () => {
  const api = useApi();
  const { user } = useAuth();
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  // Add CSS to prevent layout shift
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .monthly-summary-page {
        min-height: 100vh;
        will-change: auto;
      }
      .monthly-summary-page * {
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // State management
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().subtract(1, 'month')); // Default to last month
  const [productivityRange, setProductivityRange] = useState([0, 100]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [summaryExists, setSummaryExists] = useState(false);
  const [organization, setOrganization] = useState(null);

  // Ref to prevent duplicate check-exists API calls
  const checkExistsRef = useRef({ userId: null, month: null, year: null });
  const isCheckingRef = useRef(false);
  const abortControllerRef = useRef(null);
  
  // Ref to prevent duplicate fetchMonthlySummary API calls
  const fetchSummaryRef = useRef({ userId: null, month: null, year: null });
  const isFetchingSummaryRef = useRef(false);
  const fetchSummaryAbortControllerRef = useRef(null);

  // Check if user has standard or premium plan access (memoized)
  const hasAccess = useMemo(() => {
    const access = organization?.paymentBypassEnabled || organization?.orgPlanSelection === 'standard' || organization?.orgPlanSelection === 'premium';
    console.log('🔍 Monthly Summary Access Check:', {
      paymentBypassEnabled: organization?.paymentBypassEnabled,
      orgPlanSelection: organization?.orgPlanSelection,
      hasAccess: access
    });
    return access;
  }, [organization]);

  // Fetch organization data directly from API (like Layout.js)
  useEffect(() => {
    const fetchOrganization = async () => {
      if (user && user.id) {
        try {
          const response = await api.get('/api/v1/billing/profile');
          if (response.data.success) {
            const orgData = response.data.data;
            console.log('🔍 [MONTHLY SUMMARY] Organization data loaded:', {
              paymentBypassEnabled: orgData.paymentBypassEnabled,
              orgPlanSelection: orgData.orgPlanSelection,
              subscriptionStatus: orgData.subscriptionStatus,
              userIsActivated: orgData.userIsActivated,
              fullData: orgData
            });
            setOrganization(orgData);
          }
        } catch (error) {
          console.error('❌ Error fetching organization data:', error);
          setOrganization({});
        }
      }
    };

    fetchOrganization();
  }, [user?.id, api]);

  // Fetch users once organization is loaded and user has access
  useEffect(() => {
    if (organization && hasAccess) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, hasAccess]);

  // Check if summary exists when user/month changes
  useEffect(() => {
    if (!selectedUser || !selectedMonth) return;

    const month = selectedMonth.month() + 1;
    const year = selectedMonth.year();
    const currentKey = `${selectedUser}-${month}-${year}`;
    
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Check if already called for this exact combination
    if (
      checkExistsRef.current.userId === selectedUser &&
      checkExistsRef.current.month === month &&
      checkExistsRef.current.year === year
    ) {
      console.log('⏭️ Skipping duplicate check-exists call for:', currentKey);
      return;
    }
    
    // Check if a call is already in progress
    if (isCheckingRef.current) {
      console.log('⏭️ Call already in progress, skipping:', currentKey);
      return;
    }
    
    // Create new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Set flag and update ref immediately (synchronously)
    isCheckingRef.current = true;
    checkExistsRef.current = { userId: selectedUser, month, year };
    
    console.log('🔍 Making check-exists API call for:', currentKey);
    
    // Call the function
    checkSummaryExists(abortController.signal)
      .finally(() => {
        // Only reset if this request wasn't aborted
        if (!abortController.signal.aborted) {
          isCheckingRef.current = false;
        }
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      });
    
    // Cleanup function to cancel request if dependencies change
    return () => {
      if (abortControllerRef.current === abortController) {
        abortController.abort();
        abortControllerRef.current = null;
        isCheckingRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, selectedMonth]);

  const fetchUsers = async () => {
    if (!hasAccess) {
      console.log('⚠️ No access to monthly summary, skipping user fetch');
      return; // Don't fetch if no access
    }
    
    try {
      setLoading(true);
      console.log('📡 Fetching users from /api/v1/monthly-summary/users');
      const response = await api.get('/api/v1/monthly-summary/users');
      
      console.log('✅ Users fetched successfully:', response.data);
      
      if (response.data.success) {
        setUsers(response.data.data);
        // Auto-select current user if in list
        const currentUser = response.data.data.find(u => u._id === user?.id);
        if (currentUser) {
          setSelectedUser(currentUser._id);
        }
      } else {
        toast.error('Failed to load users: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      console.error('Error details:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        error: error.response?.data?.error
      });
      
      // Show specific error messages
      if (error.response?.status === 403) {
        toast.error('This feature is only available for Premium plan users');
      } else if (error.response?.status === 401) {
        toast.error('Please log in again to access this feature');
      } else {
        toast.error('Failed to load users: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const checkSummaryExists = async (signal) => {
    try {
      const month = selectedMonth.month() + 1;
      const year = selectedMonth.year();
      
      console.log('🔍 Calling check-exists API:', { userId: selectedUser, month, year });
      
      const response = await api.get(
        `/api/v1/monthly-summary/check-exists/${selectedUser}/${month}/${year}`,
        { signal }
      );
      
      // Check if request was aborted
      if (signal?.aborted) {
        console.log('⏭️ Request was aborted');
        return;
      }
      
      if (response.data.success) {
        setSummaryExists(response.data.data.exists);
        
        // If exists, fetch it automatically
        if (response.data.data.exists) {
          fetchMonthlySummary();
        } else {
          // If doesn't exist, automatically generate it using Perplexity
          console.log('📊 Monthly summary does not exist, generating automatically...');
          setMonthlySummary(null);
          generateMonthlySummary();
        }
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message?.includes('aborted')) {
        console.log('⏭️ Request was cancelled');
        return;
      }
      console.error('Error checking summary existence:', error);
      setSummaryExists(false);
    }
  };

  const fetchMonthlySummary = async (signal) => {
    // Prevent duplicate calls
    const month = selectedMonth.month() + 1;
    const year = selectedMonth.year();
    const currentKey = `${selectedUser}-${month}-${year}`;
    
    // Check if already fetching for this exact combination
    if (
      isFetchingSummaryRef.current ||
      (fetchSummaryRef.current.userId === selectedUser &&
       fetchSummaryRef.current.month === month &&
       fetchSummaryRef.current.year === year)
    ) {
      console.log('⏭️ Skipping duplicate fetchMonthlySummary call for:', currentKey);
      return;
    }
    
    // Cancel previous request if exists
    if (fetchSummaryAbortControllerRef.current) {
      fetchSummaryAbortControllerRef.current.abort();
      fetchSummaryAbortControllerRef.current = null;
    }
    
    // Create new AbortController if signal not provided
    const abortController = signal ? null : new AbortController();
    if (abortController) {
      fetchSummaryAbortControllerRef.current = abortController;
    }
    const requestSignal = signal || abortController.signal;
    
    // Set flag and update ref immediately
    isFetchingSummaryRef.current = true;
    fetchSummaryRef.current = { userId: selectedUser, month, year };
    
    try {
      setLoading(true);
      
      console.log('🔍 Calling fetchMonthlySummary API:', { userId: selectedUser, month, year });
      
      const response = await api.get(
        `/api/v1/monthly-summary/${selectedUser}/${month}/${year}`,
        { signal: requestSignal }
      );
      
      // Check if request was aborted
      if (requestSignal?.aborted) {
        console.log('⏭️ fetchMonthlySummary request was aborted');
        return;
      }
      
      if (response.data.success) {
        setMonthlySummary(response.data.data);
        toast.success('Monthly summary loaded successfully');
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message?.includes('aborted')) {
        console.log('⏭️ fetchMonthlySummary request was cancelled');
        return;
      }
      
      console.error('Error fetching monthly summary:', error);
      if (error.response?.status === 404) {
        toast.error('Monthly summary not found');
        setMonthlySummary(null);
      } else {
        toast.error('Failed to load monthly summary');
      }
    } finally {
      // Only reset if this request wasn't aborted
      if (!requestSignal?.aborted) {
        isFetchingSummaryRef.current = false;
      }
      if (abortController && fetchSummaryAbortControllerRef.current === abortController) {
        fetchSummaryAbortControllerRef.current = null;
      }
      setLoading(false);
    }
  };

  const generateMonthlySummary = async (forceRegenerate = false) => {
    try {
      setGenerating(true);
      const month = selectedMonth.month() + 1;
      const year = selectedMonth.year();
      
      const response = await api.post('/api/v1/monthly-summary/generate', {
        userId: selectedUser,
        month,
        year,
        productivityRange: {
          min: productivityRange[0],
          max: productivityRange[1]
        },
        forceRegenerate: forceRegenerate
      });
      
      if (response.data.success) {
        setMonthlySummary(response.data.data);
        setSummaryExists(true);
        toast.success(response.data.message || 'Monthly summary generated successfully!');
      } else if (response.data.skipped) {
        toast.error(response.data.message);
      } else if (response.data.insufficientData) {
        toast.error(response.data.message);
      } else {
        toast.error(response.data.message || 'Failed to generate monthly summary');
      }
    } catch (error) {
      console.error('Error generating monthly summary:', error);
      toast.error(error.response?.data?.message || 'Failed to generate monthly summary');
    } finally {
      setGenerating(false);
    }
  };

  // Animation variants - optimized to prevent jerk
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        duration: 0.2
      }
    }
  };

  // Use animation variants from utils - using listItemVariants for individual items
  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  // Show loading while checking organization (brief moment)
  if (!organization) {
    console.log('⏳ Waiting for organization data...');
    return (
      <div className="min-h-screen p-6 bg-app-base monthly-summary-page">
        <div style={{ opacity: 1 }}>
          {/* Header Skeleton */}
          <div className="mb-6">
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: 300, style: { marginBottom: 8 } }} />
            <Skeleton active paragraph={{ rows: 1, width: 400 }} title={false} />
          </div>

          {/* Filters Card Skeleton */}
          <Card className="mb-6 shadow-lg bg-app-base" bordered={false}>
            <Skeleton active paragraph={{ rows: 0 }} title={{ width: 150, style: { marginBottom: 24 } }} />
            <Row gutter={[24, 16]}>
              {[1, 2].map((item) => (
                <Col xs={24} md={8} key={item}>
                  <Skeleton active paragraph={{ rows: 2 }} title={{ width: 120 }} />
                </Col>
              ))}
            </Row>
          </Card>

          {/* Statistics Cards Skeleton */}
          <Row gutter={[16, 16]} className="mb-6">
            {[1, 2, 3, 4].map((item) => (
              <Col xs={12} md={6} key={item}>
                <Card 
                  className="text-center shadow-lg border-0 h-full"
                  style={{ 
                    minHeight: '180px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    background: isDark 
                      ? 'rgba(255, 255, 255, 0.02)' 
                      : 'rgba(0, 0, 0, 0.01)'
                  }}
                >
                  <Skeleton 
                    active 
                    avatar={{ shape: 'circle', size: 56 }}
                    paragraph={{ rows: 2, width: ['60%', '40%'] }}
                    title={false}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    );
  }

  // If bypass is enabled, skip all access checks
  if (organization?.paymentBypassEnabled) {
    console.log('✅ Payment bypass enabled - granting access');
    // Continue to render the page
  } 
  // Otherwise check if user has required plan
  else if (!hasAccess) {
    console.log('❌ No access - showing upgrade screen');
    return (
      <div className="min-h-screen p-6 bg-app-base">
        <motion.div
          variants={fadeInVariants}
          initial="hidden"
          animate="visible"
        >
          <Card className="max-w-2xl mx-auto text-center">
            <WarningOutlined style={{ fontSize: '64px', color: '#faad14' }} />
            <Title level={2} className="mt-4">Premium Feature</Title>
            <Paragraph className="text-lg">
              Monthly Summary is available on Standard and Premium plans. Please upgrade your plan to access this functionality.
            </Paragraph>
            <Button type="primary" size="large" href="/dashboard/settings?section=billing">
              Upgrade Plan
            </Button>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-app-base monthly-summary-page">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="mb-6">
          <Title level={2} className="mb-2 flex items-center">
            <BarChartOutlined className="mr-3" />
            Monthly Summary
          </Title>
          <Text type="secondary" className="text-base">
            Generate comprehensive monthly insights using AI-powered analysis
          </Text>
        </div>

        {/* Filters Section - Enhanced Design */}
        <motion.div variants={itemVariants}>
          <Card 
            className="mb-6 shadow-lg bg-app-base" 
            bordered={false}
          >
            <Title level={4} className="mb-6 flex items-center gap-2">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 bg-opacity-10">
                <CalendarOutlined className="text-blue-500" />
              </div>
              Filters
            </Title>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={8}>
                <div className="filter-item" style={{ position: 'relative', overflow: 'visible' }}>
                  <label className="block mb-3 font-semibold text-base flex items-center gap-2">
                    <CalendarOutlined className="text-blue-500" />
                    Select Month
                  </label>
                  <DatePicker
                    picker="month"
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    className="w-full month-picker-custom"
                    size="large"
                    disabledDate={(current) => {
                      if (!current) return false;
                      const now = dayjs();
                      // Disable current month and future months
                      return current.isSame(now, 'month') || current.isAfter(now, 'month');
                    }}
                    style={{ borderRadius: '8px', width: '100%' }}
                    getPopupContainer={(trigger) => trigger.closest('.monthly-summary-page') || document.body}
                    popupClassName="month-picker-dropdown-custom monthly-summary-picker"
                  />
                </div>
              </Col>
              
              <Col xs={24} md={8}>
                <div className="filter-item">
                  <label className="block mb-3 font-semibold text-base flex items-center gap-2">
                    <UserOutlined className="text-green-500" />
                    Select User
                  </label>
                  <Select
                    value={selectedUser}
                    onChange={setSelectedUser}
                    className="w-full"
                    size="large"
                    showSearch
                    optionFilterProp="children"
                    placeholder="Choose a user"
                    loading={loading}
                    style={{ borderRadius: '8px' }}
                  >
                    {users.map(user => (
                      <Option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName} ({user.position || 'Team Member'})
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>

              {/* <Col xs={24} md={8}>
                <div className="filter-item">
                  <label className="block mb-3 font-semibold text-base flex items-center gap-2">
                    <RiseOutlined className="text-yellow-500" />
                    Productivity Range
                  </label>
                  <div className="pt-2">
                    <Slider
                      range
                      min={0}
                      max={100}
                      value={productivityRange}
                      onChange={setProductivityRange}
                      marks={{
                        0: '0',
                        50: '50',
                        100: '100'
                      }}
                      tooltip={{
                        formatter: (value) => `${value}%`
                      }}
                    />
                    <div className="text-center mt-3">
                      <Tag color="blue" className="px-4 py-1 text-base font-semibold">
                        {productivityRange[0]}% - {productivityRange[1]}%
                      </Tag>
                    </div>
                  </div>
                </div>
              </Col> */}
            </Row>

            <Divider className="my-6" />

            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                {summaryExists && (
                  <Tag 
                    color="success" 
                    icon={<CheckCircleOutlined />}
                    className="px-4 py-2 text-base font-medium"
                  >
                    Summary Already Generated
                  </Tag>
                )}
              </div>
              <Space size="middle">
                {summaryExists && (
                  <Button
                    size="large"
                    icon={<CalendarOutlined />}
                    onClick={fetchMonthlySummary}
                    loading={loading}
                    disabled={!selectedUser || generating}
                    className="px-6"
                  >
                    View Summary
                  </Button>
                )}
                <Button
                  type="primary"
                  size="large"
                  icon={<BulbOutlined />}
                  onClick={() => generateMonthlySummary(summaryExists)}
                  loading={generating}
                  disabled={!selectedUser || loading}
                  className="px-6"
                >
                  {summaryExists ? 'Regenerate Summary' : 'Generate Summary'}
                </Button>
              </Space>
            </div>
          </Card>
        </motion.div>

        {/* Loading State - Skeleton Loader */}
        {(loading || generating) && (
          <div>
            {/* Statistics Cards Skeleton */}
            <Row gutter={[16, 16]} className="mb-6">
              {[1, 2, 3, 4].map((item) => (
                <Col xs={12} md={6} key={item}>
                  <Card 
                    className="text-center shadow-lg border-0 h-full"
                    style={{ 
                      minHeight: '180px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      background: isDark 
                        ? 'rgba(255, 255, 255, 0.02)' 
                        : 'rgba(0, 0, 0, 0.01)'
                    }}
                  >
                    <Skeleton 
                      active 
                      avatar={{ shape: 'circle', size: 56 }}
                      paragraph={{ rows: 2, width: ['60%', '40%'] }}
                      title={false}
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Collapsible Sections Skeleton */}
            <Collapse 
              className="mb-6"
              expandIconPosition="end"
              bordered={false}
              size="large"
            >
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Panel
                  key={item}
                  header={
                    <div className="flex items-center gap-3">
                      <Skeleton.Avatar 
                        active 
                        size={40} 
                        shape="square"
                        style={{ 
                          background: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Skeleton 
                        active 
                        title={{ width: 200, style: { marginBottom: 0 } }}
                        paragraph={false}
                      />
                    </div>
                  }
                  className="mb-3"
                >
                  <div className="p-4">
                    <Skeleton 
                      active 
                      paragraph={{ rows: 4, width: ['100%', '95%', '90%', '85%'] }}
                      title={false}
                    />
                  </div>
                </Panel>
              ))}
            </Collapse>

            {/* Generating Message */}
            {generating && (
              <div className="text-center py-4">
                <Text type="secondary" className="text-base flex items-center justify-center gap-2">
                  <BulbOutlined className="text-blue-500" />
                  Generating monthly insights with AI...
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Summary Display */}
        <AnimatePresence>
          {!loading && !generating && monthlySummary && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {/* Statistics Cards - Enhanced Design with Equal Height */}
              <motion.div variants={itemVariants}>
                <Row gutter={[16, 16]} className="mb-6">
                  <Col xs={12} md={6}>
                    <Card 
                      className="text-center shadow-lg hover:shadow-xl transition-all duration-300 border-0 h-full"
                      style={{ 
                        background: isDark 
                          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)' 
                          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.1) 100%)',
                        minHeight: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <div className="mb-3">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-500 bg-opacity-20 mb-3">
                          <ClockCircleOutlined className="text-3xl text-blue-500" />
                        </div>
                      </div>
                      <Statistic
                        title={<Text className="text-sm font-semibold opacity-70 block mb-1">Total Hours</Text>}
                        value={monthlySummary.totalHoursWorked}
                        precision={1}
                        suffix="hrs"
                        valueStyle={{ fontSize: '28px', fontWeight: 'bold' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card 
                      className="text-center shadow-lg hover:shadow-xl transition-all duration-300 border-0 h-full"
                      style={{ 
                        background: isDark 
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)' 
                          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(110, 231, 183, 0.1) 100%)',
                        minHeight: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <div className="mb-3">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-500 bg-opacity-20 mb-3">
                          <CalendarOutlined className="text-3xl text-green-500" />
                        </div>
                      </div>
                      <Statistic
                        title={<Text className="text-sm font-semibold opacity-70 block mb-1">Active Days</Text>}
                        value={monthlySummary.activeDays}
                        suffix="days"
                        valueStyle={{ fontSize: '28px', fontWeight: 'bold' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card 
                      className="text-center shadow-lg hover:shadow-xl transition-all duration-300 border-0 h-full"
                      style={{ 
                        background: isDark 
                          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)' 
                          : 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(252, 211, 77, 0.1) 100%)',
                        minHeight: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <div className="mb-3">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-yellow-500 bg-opacity-20 mb-3">
                          <TrophyOutlined className="text-3xl text-yellow-500" />
                        </div>
                      </div>
                      <Statistic
                        title={<Text className="text-sm font-semibold opacity-70 block mb-1">Avg Productivity</Text>}
                        value={monthlySummary.avgProductivityScore}
                        precision={1}
                        suffix="/100"
                        valueStyle={{ fontSize: '28px', fontWeight: 'bold' }}
                      />
                      <Progress
                        percent={monthlySummary.avgProductivityScore}
                        strokeColor={{
                          '0%': '#f59e0b',
                          '100%': '#fbbf24',
                        }}
                        showInfo={false}
                        strokeWidth={6}
                        className="mt-3 px-4"
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card 
                      className="text-center shadow-lg hover:shadow-xl transition-all duration-300 border-0 h-full"
                      style={{ 
                        background: isDark 
                          ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)' 
                          : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(196, 181, 253, 0.1) 100%)',
                        minHeight: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <div className="mb-3">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-500 bg-opacity-20 mb-3">
                          <ProjectOutlined className="text-3xl text-purple-500" />
                        </div>
                      </div>
                      <Statistic
                        title={<Text className="text-sm font-semibold opacity-70 block mb-1">Projects</Text>}
                        value={monthlySummary.totalProjects}
                        valueStyle={{ fontSize: '28px', fontWeight: 'bold' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </motion.div>

              {/* All Sections Collapsible - Enhanced Design */}
              <motion.div variants={itemVariants}>
                <Collapse 
                  defaultActiveKey={['monthlyOverview']}
                  className="mb-6 modern-collapse"
                  expandIconPosition="end"
                  bordered={false}
                  size="large"
                >
                  {/* Monthly Overview - Expanded by default */}
                  <Panel 
                    header={
                      <div className="flex items-center gap-3">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500 bg-opacity-10">
                          <BulbOutlined className="text-xl text-blue-500" />
                        </div>
                        <span className="text-lg font-semibold">Monthly Overview</span>
                      </div>
                    } 
                    key="monthlyOverview"
                    className="mb-3"
                  >
                    <div className="p-4 rounded-lg" style={{ 
                      background: isDark ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.02)'
                    }}>
                      {Array.isArray(monthlySummary.monthlyOverview) ? (
                        <ul className="space-y-3 pl-0 list-none mb-0">
                          {monthlySummary.monthlyOverview.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-3 pb-3 border-b border-app-subtle last:border-0 last:pb-0">
                              <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 bg-opacity-20 flex-shrink-0 mt-0.5">
                                <BulbOutlined className="text-sm text-blue-500" />
                              </div>
                              <Text className="text-base flex-1">{point}</Text>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Paragraph className="text-base whitespace-pre-line leading-relaxed mb-0">
                          {monthlySummary.monthlyOverview}
                        </Paragraph>
                      )}
                    </div>
                  </Panel>

                  {/* Projects Analysis */}
                  {monthlySummary.projectsAnalysis && (
                    <Panel 
                      header={
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500 bg-opacity-10">
                            <ProjectOutlined className="text-xl text-purple-500" />
                          </div>
                          <span className="text-lg font-semibold">Projects & Time Distribution</span>
                        </div>
                      } 
                      key="projectsAnalysis"
                      className="mb-3"
                    >
                      <div className="p-4 rounded-lg mb-4" style={{ 
                        background: isDark ? 'rgba(139, 92, 246, 0.05)' : 'rgba(139, 92, 246, 0.02)'
                      }}>
                        {Array.isArray(monthlySummary.projectsAnalysis) ? (
                          <ul className="space-y-3 pl-0 list-none mb-0">
                            {monthlySummary.projectsAnalysis.map((point, idx) => (
                              <li key={idx} className="flex items-start gap-3 pb-3 border-b border-app-subtle last:border-0 last:pb-0">
                                <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 bg-opacity-20 flex-shrink-0 mt-0.5">
                                  <ProjectOutlined className="text-sm text-purple-500" />
                                </div>
                                <Text className="text-base flex-1">{point}</Text>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Paragraph className="text-base whitespace-pre-line leading-relaxed mb-0">
                            {monthlySummary.projectsAnalysis}
                          </Paragraph>
                        )}
                      </div>

                      {monthlySummary.projects && monthlySummary.projects.length > 0 && (
                        <>
                          <Divider orientation="left" className="my-4">
                            <Text strong className="text-base">Project Breakdown</Text>
                          </Divider>
                          <Row gutter={[12, 12]}>
                            {monthlySummary.projects.slice(0, 6).map((project, idx) => (
                              <Col xs={24} sm={12} lg={8} key={idx}>
                                <Card 
                                  size="small" 
                                  className="h-full hover:shadow-lg hover:scale-105 transition-all duration-300 border-0"
                                  style={{ 
                                    background: isDark 
                                      ? 'rgba(139, 92, 246, 0.08)' 
                                      : 'rgba(139, 92, 246, 0.04)',
                                    borderLeft: '3px solid #8b5cf6'
                                  }}
                                >
                                  <div className="mb-3">
                                    <div className="flex justify-between items-start mb-2">
                                      <Text strong className="text-base">{project.projectName}</Text>
                                      <Tag color="purple" className="ml-2 font-semibold">
                                        {project.percentageOfMonth.toFixed(1)}%
                                      </Tag>
                                    </div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <ClockCircleOutlined className="text-xs opacity-60" />
                                      <Text type="secondary" className="text-sm">
                                        {(project.totalTimeSpent / 60).toFixed(1)} hours
                                      </Text>
                                    </div>
                                  </div>
                                  <Progress
                                    percent={project.percentageOfMonth}
                                    strokeColor={{
                                      '0%': '#8b5cf6',
                                      '100%': '#a78bfa',
                                    }}
                                    showInfo={false}
                                    strokeWidth={8}
                                  />
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </>
                      )}
                    </Panel>
                  )}

                  {/* Time Management Analysis */}
                  {/* {monthlySummary.timeManagementAnalysis && (
                    <Panel 
                      header={
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-green-500 bg-opacity-10">
                            <ClockCircleOutlined className="text-xl text-green-500" />
                          </div>
                          <span className="text-lg font-semibold">Time Management</span>
                        </div>
                      } 
                      key="timeManagement"
                      className="mb-3"
                    >
                      <div className="p-4 rounded-lg" style={{ 
                        background: isDark ? 'rgba(16, 185, 129, 0.05)' : 'rgba(16, 185, 129, 0.02)'
                      }}>
                        <Paragraph className="text-base whitespace-pre-line leading-relaxed mb-0">
                          {monthlySummary.timeManagementAnalysis}
                        </Paragraph>
                      </div>
                    </Panel>
                  )} */}

                  {/* Strengths */}
                  {monthlySummary.strengthsIdentified && monthlySummary.strengthsIdentified.length > 0 && (
                    <Panel
                      header={
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-green-500 bg-opacity-10">
                            <CheckCircleOutlined className="text-xl text-green-500" />
                          </div>
                          <span className="text-lg font-semibold">Strengths</span>
                          <Tag color="green" className="ml-2 font-semibold">{monthlySummary.strengthsIdentified.length}</Tag>
                        </div>
                      }
                      key="strengths"
                      className="mb-3"
                    >
                      <div className="space-y-0">
                        {monthlySummary.strengthsIdentified.map((strength, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-start gap-3 p-3 rounded-lg hover:shadow-md transition-all duration-200"
                            style={{ 
                              background: isDark ? 'rgba(16, 185, 129, 0.05)' : 'rgba(16, 185, 129, 0.02)',
                              marginBottom: idx < monthlySummary.strengthsIdentified.length - 1 ? '8px' : '0'
                            }}
                          >
                            <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 bg-opacity-20 flex-shrink-0 mt-0.5">
                              <CheckCircleOutlined className="text-sm text-green-500" />
                            </div>
                            <Text className="text-base flex-1">{strength}</Text>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  )}

                  {/* What Could Have Been Done Better */}
                  {monthlySummary.whatCouldHaveBeenDoneBetter && monthlySummary.whatCouldHaveBeenDoneBetter.length > 0 && (
                    <Panel
                      header={
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500 bg-opacity-10">
                            <BulbOutlined className="text-xl text-purple-500" />
                          </div>
                          <span className="text-lg font-semibold">What Could Have Been Done Better</span>
                          <Tag color="purple" className="ml-2 font-semibold">{monthlySummary.whatCouldHaveBeenDoneBetter.length}</Tag>
                        </div>
                      }
                      key="whatCouldHaveBeenDoneBetter"
                      className="mb-3"
                    >
                      <div className="space-y-4">
                        {(() => {
                          // Parse and group suggestions by category
                          const groupedSuggestions = {};
                          const standaloneSuggestions = [];
                          
                          monthlySummary.whatCouldHaveBeenDoneBetter.forEach((suggestion) => {
                            // Check if suggestion contains a category header (e.g., "Position Analysis:")
                            const lines = suggestion.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                            
                            let currentCategory = null;
                            const subPoints = [];
                            
                            lines.forEach((line) => {
                              // Check if line is a category header (ends with colon)
                              if (line.match(/^[^:]+:\s*$/)) {
                                // Save previous category if exists
                                if (currentCategory && subPoints.length > 0) {
                                  if (!groupedSuggestions[currentCategory]) {
                                    groupedSuggestions[currentCategory] = [];
                                  }
                                  groupedSuggestions[currentCategory].push(...subPoints);
                                  subPoints.length = 0;
                                }
                                currentCategory = line.replace(':', '').trim();
                              } else if (line.match(/^[•\-\*]\s*(.+)$/)) {
                                // This is a bullet point
                                const point = line.replace(/^[•\-\*]\s*/, '').trim();
                                if (point.length > 0) {
                                  subPoints.push(point);
                                }
                              } else if (currentCategory) {
                                // Regular line under a category
                                if (line.length > 0) {
                                  subPoints.push(line);
                                }
                              } else {
                                // Standalone suggestion
                                standaloneSuggestions.push(line);
                              }
                            });
                            
                            // Save last category
                            if (currentCategory && subPoints.length > 0) {
                              if (!groupedSuggestions[currentCategory]) {
                                groupedSuggestions[currentCategory] = [];
                              }
                              groupedSuggestions[currentCategory].push(...subPoints);
                            }
                            
                            // If no category found, treat entire suggestion as standalone
                            if (!currentCategory && lines.length > 0) {
                              const fullText = lines.join(' ');
                              if (!standaloneSuggestions.includes(fullText)) {
                                standaloneSuggestions.push(fullText);
                              }
                            }
                          });
                          
                          // Remove duplicates within each category
                          Object.keys(groupedSuggestions).forEach(category => {
                            groupedSuggestions[category] = [...new Set(groupedSuggestions[category])];
                          });
                          
                          return (
                            <>
                              {/* Render grouped suggestions */}
                              {Object.entries(groupedSuggestions).map(([category, subPoints], categoryIdx) => (
                                <div 
                                  key={categoryIdx}
                                  className="mb-4 p-4 rounded-lg"
                                  style={{ 
                                    background: isDark ? 'rgba(139, 92, 246, 0.05)' : 'rgba(139, 92, 246, 0.02)',
                                    borderLeft: '4px solid #8b5cf6'
                                  }}
                                >
                                  <Text strong className="text-base mb-3 block" style={{ color: isDark ? '#a78bfa' : '#7c3aed', fontSize: '16px', fontWeight: 600 }}>
                                    {category}:
                                  </Text>
                                  <ul className="space-y-2 ml-4" style={{ listStyle: 'none', padding: 0 }}>
                                    {subPoints.map((point, pointIdx) => (
                                      <li key={pointIdx} className="text-base flex items-start gap-2">
                                        <span className="text-purple-500 mt-1.5" style={{ fontSize: '12px' }}>•</span>
                                        <Text className="flex-1">{point}</Text>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                              
                              {/* Render standalone suggestions */}
                              {standaloneSuggestions.map((suggestion, idx) => (
                                <div 
                                  key={`standalone-${idx}`}
                                  className="flex items-start gap-3 p-3 rounded-lg hover:shadow-md transition-all duration-200"
                                  style={{ 
                                    background: isDark ? 'rgba(139, 92, 246, 0.05)' : 'rgba(139, 92, 246, 0.02)',
                                    marginBottom: idx < standaloneSuggestions.length - 1 ? '8px' : '0'
                                  }}
                                >
                                  <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 bg-opacity-20 flex-shrink-0 mt-0.5">
                                    <BulbOutlined className="text-sm text-purple-500" />
                                  </div>
                                  <Text className="text-base flex-1">{suggestion}</Text>
                                </div>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    </Panel>
                  )}

                  {/* Issues */}
                  {monthlySummary.issuesIdentified && monthlySummary.issuesIdentified.length > 0 && (
                    <Panel
                      header={
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-orange-500 bg-opacity-10">
                            <WarningOutlined className="text-xl text-orange-500" />
                          </div>
                          <span className="text-lg font-semibold">Areas to Address</span>
                          <Tag color="orange" className="ml-2 font-semibold">{monthlySummary.issuesIdentified.length}</Tag>
                        </div>
                      }
                      key="issues"
                      className="mb-3"
                    >
                      <div className="space-y-0">
                        {monthlySummary.issuesIdentified.map((issue, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-start gap-3 p-3 rounded-lg hover:shadow-md transition-all duration-200"
                            style={{ 
                              background: isDark ? 'rgba(249, 115, 22, 0.05)' : 'rgba(249, 115, 22, 0.02)',
                              marginBottom: idx < monthlySummary.issuesIdentified.length - 1 ? '8px' : '0'
                            }}
                          >
                            <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 bg-opacity-20 flex-shrink-0 mt-0.5">
                              <WarningOutlined className="text-sm text-orange-500" />
                            </div>
                            <Text className="text-base flex-1">{issue}</Text>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  )}

                  {/* Improvements */}
                  {monthlySummary.improvementSuggestions && monthlySummary.improvementSuggestions.length > 0 && (
                    <Panel
                      header={
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500 bg-opacity-10">
                            <BulbOutlined className="text-xl text-blue-500" />
                          </div>
                          <span className="text-lg font-semibold">Action Items</span>
                          <Tag color="blue" className="ml-2 font-semibold">{monthlySummary.improvementSuggestions.length}</Tag>
                        </div>
                      }
                      key="improvements"
                      className="mb-3"
                    >
                      <div className="space-y-0">
                        {monthlySummary.improvementSuggestions.map((suggestion, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-start gap-3 p-3 rounded-lg hover:shadow-md transition-all duration-200"
                            style={{ 
                              background: isDark ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.02)',
                              marginBottom: idx < monthlySummary.improvementSuggestions.length - 1 ? '8px' : '0'
                            }}
                          >
                            <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 bg-opacity-20 flex-shrink-0 mt-0.5">
                              <BulbOutlined className="text-sm text-blue-500" />
                            </div>
                            <Text className="text-base flex-1">{suggestion}</Text>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  )}

                  {/* Peak Productivity Periods */}
                  {/* {monthlySummary.peakProductivityPeriods && monthlySummary.peakProductivityPeriods.length > 0 && (
                    <Panel
                      header={
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-yellow-500 bg-opacity-10">
                            <TrophyOutlined className="text-xl text-yellow-500" />
                          </div>
                          <span className="text-lg font-semibold">Peak Performance Periods</span>
                          <Tag color="gold" className="ml-2 font-semibold">{monthlySummary.peakProductivityPeriods.length}</Tag>
                        </div>
                      }
                      key="peakPeriods"
                      className="mb-3"
                    >
                      <Row gutter={[12, 12]}>
                        {monthlySummary.peakProductivityPeriods.map((period, idx) => (
                          <Col xs={24} sm={12} md={8} lg={6} key={idx}>
                            <Card 
                              size="small" 
                              className="text-center h-full hover:shadow-lg hover:scale-105 transition-all duration-300 border-0"
                              style={{ 
                                background: isDark 
                                  ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)' 
                                  : 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(252, 211, 77, 0.1) 100%)',
                                borderTop: '3px solid #f59e0b'
                              }}
                            >
                              <div className="mb-2">
                                <TrophyOutlined className="text-2xl text-yellow-500 mb-2" />
                              </div>
                              <Text strong className="text-lg block mb-2">{period.period}</Text>
                              <Tag 
                                color="gold" 
                                className="text-base mb-3 px-3 py-1 font-bold"
                                style={{ fontSize: '14px' }}
                              >
                                {period.avgScore}/100
                              </Tag>
                              <Divider className="my-3" style={{ margin: '12px 0' }} />
                              <Text className="text-sm opacity-80">{period.description}</Text>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Panel>
                  )} */}
                </Collapse>
              </motion.div>

              {/* Metadata */}
              <motion.div variants={itemVariants}>
                <Card size="small" className="bg-app-muted">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Text type="secondary">
                        Generated: {new Date(monthlySummary.generatedAt).toLocaleString()}
                      </Text>
                    </Col>
                    <Col span={12} className="text-right">
                      <Text type="secondary">
                        Data Source: {monthlySummary.dataSource?.hourlySummariesCount} hourly summaries, {monthlySummary.dataSource?.dailySummariesCount} daily summaries
                      </Text>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {!loading && !generating && !monthlySummary && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="text-center py-12">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <Title level={4}>No Monthly Summary Available</Title>
                    <Paragraph>
                      Select a user and month, then click "Generate Summary" to create an AI-powered monthly analysis.
                    </Paragraph>
                  </div>
                }
              />
            </Card>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default MonthlySummary;

